import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, Star, Users, Award, Clock, Briefcase, Calculator, FileBadge, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { startupServices } from '../data/services';
import ServiceCard from '../components/common/ServiceCard';

const Home = () => {
  const stats = [
    { number: '6+', label: 'Years Experience', icon: Clock },
    { number: '2500+', label: 'Happy Clients', icon: Users },
    { number: '99.9%', label: 'Success Rate', icon: Award },
    { number: '10+', label: 'Expert Team', icon: Briefcase },
  ];

  const serviceCategories = [
    { title: 'Startup Services', icon: Briefcase, path: '/startup-services', description: 'Company Registration, LLP, OPC & more' },
    { title: 'GST Services', icon: Calculator, path: '/gst', description: 'Registration, Returns & Compliance' },
    { title: 'Registration', icon: FileBadge, path: '/registration', description: 'Licenses, Trademarks & Certifications' },
    { title: 'Compliances', icon: ShieldCheck, path: '/compliances', description: 'Tax Filing & Annual Compliances' },
  ];

  const testimonials = [
    {
      name: 'Rajjev Kumar Rana',
      company: 'Oredia Therapeutics Pvt Ltd',
      text: 'GK Legal Solutions made our company registration process seamless. Their expert team guided us through every step with professionalism and dedication.',
      rating: 5
    },
    {
      name: 'Sandesh Gawai',
      company: 'Gameksha Interactive Pvt Ltd',
      text: 'Excellent service for our private limited company setup. The team handled all documentation efficiently and kept us updated throughout the process.',
      rating: 5
    },
    {
      name: 'Vaishali Wagh',
      company: 'Takshila Foundation',
      text: 'Outstanding support for our NGO registration and 12A/80G compliance. Highly professional team with deep knowledge of non-profit regulations.',
      rating: 5
    }
  ];

  const process = [
    { step: '01', title: 'Consultation', description: 'Share your requirements with our experts' },
    { step: '02', title: 'Documentation', description: 'We prepare and verify all documents' },
    { step: '03', title: 'Processing', description: 'Swift filing with government authorities' },
    { step: '04', title: 'Delivery', description: 'Receive your registration certificate' },
  ];

  return (
    <>
      <Helmet>
        <title>GK Legal Solutions - Company Registration, GST & Compliance Services in India</title>
        <meta name="description" content="GK Legal Solutions offers expert Company Registration, GST Registration, Trademark, FSSAI License, Income Tax Filing & Compliance services. Trusted by 2500+ businesses in India." />
        <meta name="keywords" content="company registration, GST registration, private limited company, LLP, trademark registration, FSSAI license, income tax filing, Mumbai" />
        <link rel="canonical" href="https://gklegalsolutions.com/" />
      </Helmet>
      
      <div className="min-h-screen" data-testid="home-page">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-slate-50 to-blue-50/50 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 right-20 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          </div>

          <div className="container-custom relative z-10 py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-fade-in">
                <span className="inline-block font-body text-sm font-semibold text-primary bg-primary/10 px-4 py-2 rounded-full mb-6">
                  Trusted by 2500+ Businesses
                </span>
                <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.1] mb-6">
                  Your Legal & <span className="text-primary">Tax Partner</span> for Business Growth
                </h1>
                <p className="font-body text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                  From company registration to compliance management, we provide comprehensive legal and tax solutions to help your business thrive.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="btn-primary flex items-center gap-2" data-testid="hero-get-started-btn">
                      Get Started
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/about">
                    <Button variant="outline" className="btn-secondary" data-testid="hero-learn-more-btn">
                      Learn More
                    </Button>
                  </Link>
                </div>

                <div className="flex items-center gap-6 mt-10 pt-10 border-t border-slate-200">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center">
                        <span className="font-body text-xs font-semibold text-slate-600">{i}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="font-body text-sm text-slate-600">Rated 4.9/5 by 500+ clients</p>
                  </div>
                </div>
              </div>

              <div className="relative animate-fade-in animate-delay-200">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800"
                    alt="Professional legal consultation"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
                </div>
                
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs animate-fade-in animate-delay-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-heading text-2xl font-bold text-slate-900">99.9%</p>
                      <p className="font-body text-sm text-slate-600">Success Rate</p>
                    </div>
                  </div>
                  <p className="font-body text-xs text-slate-500">Trusted by businesses across India</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="section-spacing bg-white" data-testid="services-section">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Our Services</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
                Comprehensive Business Solutions
              </h2>
              <p className="font-body text-lg text-slate-600">
                From startup registration to ongoing compliance, we've got all your business needs covered.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceCategories.map((category, index) => (
                <Link 
                  key={index}
                  to={category.path}
                  className="group p-8 bg-slate-50 rounded-xl hover:bg-primary transition-all duration-300"
                  data-testid={`service-category-${category.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <category.icon className="w-12 h-12 text-primary group-hover:text-white mb-6 transition-colors" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 group-hover:text-white mb-2 transition-colors">
                    {category.title}
                  </h3>
                  <p className="font-body text-sm text-slate-600 group-hover:text-white/80 transition-colors">
                    {category.description}
                  </p>
                  <ArrowRight className="w-5 h-5 text-primary group-hover:text-white mt-4 group-hover:translate-x-2 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="section-spacing bg-slate-50">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
              <div>
                <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Popular Services</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3">
                  Start Your Business Journey
                </h2>
              </div>
              <Link to="/startup-services" className="mt-4 md:mt-0">
                <Button variant="outline" className="btn-secondary flex items-center gap-2">
                  View All Services
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {startupServices.slice(0, 6).map((service) => (
                <ServiceCard key={service.id} service={service} linkTo="/contact" />
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="section-spacing bg-white">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">How It Works</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
                Simple 4-Step Process
              </h2>
              <p className="font-body text-lg text-slate-600">
                We make business registration and compliance hassle-free with our streamlined process.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative text-center group">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary transition-colors">
                    <span className="font-heading text-2xl font-bold text-primary group-hover:text-white transition-colors">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-slate-600">{item.description}</p>
                  
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-slate-200">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary rounded-full"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-primary">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-8 h-8 text-white/80 mx-auto mb-3" />
                  <p className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">{stat.number}</p>
                  <p className="font-body text-sm text-white/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="section-spacing bg-slate-900">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Testimonials</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
                What Our Clients Say
              </h2>
              <p className="font-body text-lg text-slate-400">
                Join thousands of satisfied businesses who trust us with their legal needs.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="bg-slate-800 p-8 rounded-xl hover:bg-slate-700 transition-colors"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="font-body text-slate-300 leading-relaxed mb-6">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <span className="font-heading text-lg font-bold text-white">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-body text-white font-semibold">{testimonial.name}</p>
                      <p className="font-body text-sm text-slate-400">{testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-spacing bg-primary">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Start Your Business?
              </h2>
              <p className="font-body text-lg text-white/80 mb-8">
                Get expert guidance and register your business today. First consultation is free!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="bg-white text-primary hover:bg-slate-100 px-8 py-3 font-semibold text-sm tracking-wide uppercase" data-testid="cta-contact-btn">
                    Contact Us Now
                  </Button>
                </Link>
                <a href="tel:+919820669943">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 font-semibold text-sm tracking-wide uppercase flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Call Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
